import {html, HTMLTemplateResult} from "lit";
import {buildAssetSrc} from "../../helpers/asset-embed.helper";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {createRef, ref} from "lit/directives/ref.js";
import {GridStack, GridItemHTMLElement, GridStackWidget} from "gridstack";
import "gridstack/dist/gridstack.css";
import {fromEvent, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {container} from "tsyringe";
import {RteValueHelper} from "../../helpers/rte-value.helper";

import {AbstractLitElement} from "../abstract-lit.element";
import {BackendClient} from "../../connectivity/backend-client";
import {LinkTileHelper, type LinkTileDraft} from "../../helpers/link-tile.helper";
import {DialogHelper} from "../../helpers/dialog.helper";
import {UiHelper} from "../../helpers/ui.helper";
import {DialogCommandName} from "../ha-dialog/dialog-command.enum";
import type {DialogCommand} from "../ha-dialog/dialog-command.interface";
import type {HaDialogLinkTileEditComponent} from "../_dialogs/ha-dialog-link-tile-edit/ha-dialog-link-tile-edit.component";
import type {LinkTile} from "../../domain/entities/link-tile.entity";
import {diffTiles} from "../../domain/link-tile-diff";
import {AbstractLinkviewRepository, LINKVIEW_REPOSITORY} from "../../domain/repositories/abstract-linkview.repository";
import type {BackendLinkviewRepository} from "../../domain/repositories/backend-linkview.repository";
import type {AbstractTileComponent} from "./tiles/abstract-tile.component";
import {LinkTilePlainComponent} from "./tiles/link-tile-plain.component";
import {TILE_COMPONENT_BY_KIND} from "./tiles/tile-registry";
import {TileEventName} from "./tiles/tile-event-name.enum";

/**
 * Dashboard-creator for the Linkview mode (Stefan, 2026-09-03): a
 * user-editable grid of link tiles (text + link target + image + colour +
 * icon), draggable/resizable via Gridstack, responsive column count via
 * `columnOpts.breakpoints`, persisted through `AbstractLinkviewRepository`
 * (`LocalStorageLinkviewRepository` today — no backend endpoint exists for
 * this yet; Stefan is rebuilding this on Commands + backend storage
 * separately, see the 2026-09-04 revert of an earlier backend attempt).
 *
 * Gridstack owns the `.grid-stack` subtree's DOM imperatively (drag/resize/
 * reflow all mutate it directly) — this component deliberately does NOT let
 * Lit's `repeat()` render the tiles themselves (the two would fight over the
 * same nodes). Each Gridstack item's content is exactly ONE mounted
 * `AbstractTileComponent` subclass (Anke, 2026-09-04: "für jedes tile eine
 * eigene Component... die auf einer gemeinsamen AbstractTitleComponent
 * [sic, gemeint: AbstractTileComponent] basiert") — see `_mountTile()` —
 * never raw imperative DOM building.
 *
 * Tile properties (text/colour/link target/image/icon) are edited through
 * `ha-dialog-link-tile-edit` (Anke, 2026-09-04) — opened via `DialogHelper`
 * exactly like the other non-entity dialogs in this app; its own doc
 * comment explains why it isn't an `AbstractEntityDialogComponent`.
 *
 * This component has genuinely NO toolbar of its own (Anke, 2026-09-17: "die
 * Linkboards auf den LOCK-mechanismus umstellen, wie die Whiteboards") — a
 * toolbar at the top of the board (Bearbeiten / + Neue Kachel / Speichern /
 * Abbrechen, structured like the Todo toolbar) lives in
 * `o2d-linkview.view.ts` instead, a SIBLING in the DOM, not a parent/child —
 * exactly like the "+ Neue Kachel" signal already worked before this change
 * (2026-09-04 through 2026-09-16, when that button lived in the sidebar
 * instead). Coordinated entirely through `AbstractLinkviewRepository`:
 * `items$` for lock state, `addTileRequested$`/`saveRequested$`/
 * `cancelRequested$` for the toolbar's three action buttons reaching this
 * specific mounted instance. While a board is LOCKED (the default for a
 * freshly created one): Gridstack is `setStatic` (no drag/resize) and tiles
 * render with NEITHER their edit NOR delete button. The lock is also
 * enforced INSIDE `_openTileDialog`/`_createTile`/`_updateTile`/
 * `_removeTile` themselves (review 0025-01, MINOR), not only via the UI
 * affordances that normally call them.
 *
 * Saving now RELEASES THE LOCK and returns to view mode (2026-09-17,
 * matching Whiteboard/KnowledgeObject exactly) — the User must click
 * "Bearbeiten" again for further edits; `_onRequestSave` awaits the release
 * AFTER a successful save, same order-matters reasoning as
 * `knowledge-whiteboard.component.ts`'s own `_onSave` (a failed/slow
 * release must not make this client's own UI show "free" before the server
 * confirms it, which could surface a misleading take-over dialog to a
 * second user racing in during that gap). "Abbrechen" (`_onRequestCancel`)
 * discards any local edits — confirming first when `diffTiles()` against
 * the edit-entry baseline says something actually changed — reloads the
 * board from the server, and releases the lock the same way.
 *
 * Light DOM is inherited from AbstractLitElement (ADR-007).
 */
@customElement('dashboard-creator')
export class DashboardCreatorComponent extends AbstractLitElement {

    /** Which named Linkview this board belongs to — set once, at creation
     *  (`o2d-linkview.view.ts` keys the whole element by this value so a
     *  change always produces a fresh instance, never a live update
     *  mid-lifecycle). */
    @property({type: String})
    public linkviewUid?: string;

    /** Locked (default) = read-only: no drag/resize, tiles show neither
     *  edit nor delete. Driven entirely by `AbstractLinkviewRepository.
     *  items$` — see `connectedCallback()`/`_applyLockState()`. */
    @state()
    private _locked = true;

    /** Re-entrancy guards for `_onRequestSave`/`_onRequestCancel` (2026-09-17,
     *  same pattern as `knowledge-whiteboard.component.ts`'s `_isSaving`/
     *  `_isCancelling`) — both await at least one thing before their first
     *  state mutation, so a rapid double-click on the toolbar could otherwise
     *  fire the save/cancel flow twice. */
    private _saving = false;
    private _cancelling = false;

    /** The tile list as loaded when this session entered edit mode — what
     *  `_onRequestCancel`'s dirty-check (`diffTiles`) compares the CURRENT
     *  live layout against. Set alongside `_content`/`_tileElements` in
     *  `_mountAll()`, so it is always the same read that seeded them. */
    private _baselineTiles: LinkTile[] = [];

    private readonly _gridElRef = createRef<HTMLDivElement>();
    private _grid: GridStack | null = null;
    private readonly _linkviewRepo: AbstractLinkviewRepository = container.resolve(LINKVIEW_REPOSITORY);
    private readonly _backendClient: BackendClient = container.resolve(BackendClient);

    /**
     * Files the user has picked but that are not in the asset store yet, keyed
     * by tile id.
     *
     * They wait here until a save, and that is the whole point: the store
     * deduplicates by content hash but offers no way to DELETE anything, so a
     * file uploaded the moment it is picked survives forever even if the user
     * cancels the dialog. An entry is dropped only after the upload it belongs
     * to has succeeded — a failed save keeps every picked file, so nothing the
     * user chose is lost and a retry costs nothing (the same bytes resolve to
     * the same stored row).
     */
    private readonly _pendingImages = new Map<string, File>();

    /** Every user-authored field per tile id — the part of a tile Gridstack
     *  itself never knows about (it only owns x/y/w/h). Source of truth for
     *  `_collectTiles()` and for re-opening the edit dialog on an existing
     *  tile. */
    private readonly _content = new Map<string, LinkTileDraft>();

    /** The mounted tile-component instance per tile id — lets
     *  `_applyLockState()` update `.locked` on every tile directly, without a
     *  tag-name selector that would need extending for every new tile kind. */
    private readonly _tileElements = new Map<string, AbstractTileComponent>();

    /** The RxJS subscription currently listening for `dialog.message` on the
     *  (singleton, reused-while-open) `ha-dialog-link-tile-edit` instance, if
     *  any (review 0025-01/02). `DialogHelper.showDialog` returns the SAME
     *  element while a dialog of that type is already open — opening a
     *  second time, e.g. via a fast double-click on an edit/delete/"+ Neue
     *  Kachel" button, used to attach a second listener that was never
     *  removed, so a single Speichern-click fired both and created/updated a
     *  tile twice. `fromEvent(...).pipe(takeUntil(this._disconnected$))`
     *  (the same pattern every other subscription in this class already
     *  uses) fixes BOTH failure modes at once: unsubscribing the PREVIOUS
     *  subscription before starting a new one caps it at one listener per
     *  `_openTileDialog()` call, and `takeUntil` automatically tears it down
     *  on disconnect too — no separate manual `removeEventListener`/element
     *  bookkeeping needed in `disconnectedCallback()`. */
    private _tileDialogSubscription: Subscription | null = null;

    public override connectedCallback(): void {
        super.connectedCallback();

        // A BehaviorSubject — this ALSO fires synchronously with the current
        // lock state on subscribe, so no separate "read the initial value"
        // step is needed. Runs before firstUpdated() (Lit lifecycle order),
        // so `this._locked` is already correct by the time tiles first render.
        this._linkviewRepo.items$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((items) => {
                const locked = items.find((item) => item.uid === this.linkviewUid)?.locked ?? true;
                if (locked !== this._locked) {
                    this._applyLockState(locked);
                }
            });

        // The toolbar's "+ Neue Kachel" button (`o2d-linkview.view.ts`) —
        // see the class doc comment on why this Subject (not a direct
        // method call) is how the click reaches this specific mounted
        // instance.
        this._linkviewRepo.addTileRequested$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((uid) => {
                if (uid === this.linkviewUid) this._openTileDialog(null);
            });

        // The toolbar's "Speichern"/"Abbrechen" buttons — same signal
        // pattern as `addTileRequested$` above.
        this._linkviewRepo.saveRequested$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((uid) => {
                if (uid === this.linkviewUid) void this._onRequestSave();
            });
        this._linkviewRepo.cancelRequested$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((uid) => {
                if (uid === this.linkviewUid) void this._onRequestCancel();
            });
    }

    public override async firstUpdated(): Promise<void> {
        const gridEl = this._gridElRef.value;
        if (!gridEl) return;

        this._grid = GridStack.init({
            float: true,
            margin: 8,
            cellHeight: 90,
            column: 12,
            // Responsive column collapse (Anke's "möglichst responsive"):
            // desktop 12 → 6 at ≤1200px → 3 at ≤768px → 1 at ≤480px (stacked,
            // phone-friendly). Gridstack 13's CSS-variable engine needs no
            // extra stylesheet for this (unlike older major versions).
            columnOpts: {
                breakpoints: [
                    {w: 1200, c: 6},
                    {w: 768, c: 3},
                    {w: 480, c: 1},
                ],
            },
        }, gridEl);
        // `connectedCallback()` already set `this._locked` correctly (runs
        // first, see its own comment) — apply it to the freshly created grid.
        // Non-null: GridStack.init() returns null only without a `document` or without an
        // element, and `gridEl` was checked above.
        this._grid!.setStatic(this._locked);

        const tiles = await this._linkviewRepo.getTiles(this.linkviewUid);
        // Review Code-Lauf 01, MINOR: `disconnectedCallback()` sets `_grid =
        // null` and TS cannot narrow that across an `await` — a fast
        // navigation away while `getTiles()` is still in flight would
        // otherwise throw out of this (now async) `firstUpdated()`. Harmless
        // today (localStorage resolves within a microtask), but the whole
        // point of the async repository interface is a future backend swap,
        // where this window is real.
        if (!this._grid) return;

        this._mountAll(tiles);
    }

    /**
     * Puts a whole tile list onto the grid: `_content` first (every mount reads
     * it), then ONE `grid.load()`, then one mount per created element.
     *
     * `load()` rather than a loop of `addWidget()` calls, and that is not a
     * style choice. Gridstack keeps a cache of the reference-width layout and
     * fills it in `load()`; `addWidget()` leaves it empty. With an empty cache
     * a perfectly correct `save(false)` still hands back the COLLAPSED
     * coordinates whenever a responsive column collapse happened in between —
     * and reading the tiles is a real network round trip now, so that window
     * is real rather than theoretical.
     *
     * `load()` returns the grid, not the created elements (unlike `addWidget`),
     * so each element is looked up by `[gs-id="…"]` afterwards — the same
     * lookup `_updateTile` already uses.
     */
    private _mountAll(tiles: LinkTile[]): void {
        if (!this._grid) return;

        this._baselineTiles = tiles;
        this._content.clear();
        this._tileElements.clear();
        // A pick that was never saved dies here. Leaving edit mode discards the
        // session's changes, so a file still parked from it must not survive
        // into the next one — it would upload on the following save and write
        // back an image the user abandoned a session ago, against a tile list
        // that has since been re-read from the server.
        this._pendingImages.clear();
        for (const tile of tiles) {
            this._content.set(tile.id, tileToDraft(tile));
        }

        // `addRemove` defaults to true, so a tile the list no longer names is
        // removed from the grid — which is what makes this usable as a
        // re-mount after a fresh read, not only as a first mount.
        this._grid.load(tiles.map((tile) => ({id: tile.id, x: tile.x, y: tile.y, w: tile.w, h: tile.h})));

        for (const tile of tiles) {
            const el = this._gridElRef.value?.querySelector<GridItemHTMLElement>(`[gs-id="${tile.id}"]`);
            if (el) this._mountTile(el, tile.id);
        }
    }

    public override disconnectedCallback(): void {
        // Best-effort release when the board is left while still in edit
        // mode (another board/mode, closed view) — same as
        // `knowledge-whiteboard`/`knowledge-rte`; there is no TTL and no
        // sidebar lock button any more, so an abandoned lock could only be
        // taken over. Unsaved in-memory tile changes are lost on unmount
        // anyway (a remount re-reads the board). Fire-and-forget:
        // `disconnectedCallback` cannot await, and a hard tab close never
        // reaches it (the known limitation of every lock in this app).
        if (!this._locked && this.linkviewUid) {
            const uid = this.linkviewUid;
            this._linkviewRepo.releaseLock(uid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${uid}» on disconnect.`, releaseError);
            });
        }
        // `_tileDialogSubscription` tears itself down via `takeUntil` inside
        // super.disconnectedCallback() below (which fires `_disconnected$`)
        // — no manual unsubscribe needed here, see its own doc comment.
        this._grid?.destroy(false);
        this._grid = null;
        super.disconnectedCallback();
    }

    protected render(): HTMLTemplateResult {
        return html`
            <div class="dashboard-creator">
                <div class="grid-stack" ${ref(this._gridElRef)}></div>
            </div>
        `;
    }

    /**
     * The toolbar's "Speichern" click (`saveRequested$`, see
     * `connectedCallback()`). Saving is DELIBERATE, by button — dragging a
     * tile around while thinking must not publish half-finished states to
     * everybody on a shared page.
     *
     * Releases the lock AFTER a successful save and returns to view mode
     * (2026-09-17, matching `knowledge-whiteboard.component.ts`'s own
     * `_onSave` exactly, including the ORDER: await the release BEFORE the
     * `items$`-driven `_applyLockState(true)` transition, so this client's
     * own UI never shows "free" before the server actually confirms it — a
     * failed/slow release would otherwise risk a misleading take-over
     * dialog for a second user racing in during that gap).
     *
     * A failed release is surfaced (`_notifyReleaseFailed`): unlike the
     * Whiteboard, which flips its own local `_mode`, this board's mode
     * follows `items$`, so after a failed release the toolbar keeps showing
     * "Speichern"/"Abbrechen" — the User must be told the content IS saved
     * and that pressing either button again retries the release.
     */
    private async _onRequestSave(): Promise<void> {
        if (this._locked || this._saving || this._cancelling) return;
        this._saving = true;
        try {
            await this._save();
        } finally {
            this._saving = false;
        }
    }

    /**
     * The toolbar's "Abbrechen" click (`cancelRequested$`). Asks for
     * confirmation FIRST when `diffTiles()` against the edit-entry baseline
     * says something actually changed (mirroring the Whiteboard's own
     * `_saveButtonEnabled`-gated confirm) — declining leaves everything
     * untouched, still in edit mode. When confirmed (or when there was
     * nothing dirty to begin with): reloads the board from the server (the
     * same read `_reloadTiles()` already performs on the step INTO edit
     * mode — reusable here unchanged, since it only depends on `_grid`
     * existing, not on lock state) and releases the lock, same ORDER
     * reasoning as `_onRequestSave` above.
     */
    private async _onRequestCancel(): Promise<void> {
        if (this._locked || this._saving || this._cancelling || !this.linkviewUid) return;
        this._cancelling = true;
        try {
            const dirty = diffTiles(this.linkviewUid, this._baselineTiles, this._collectTiles()).length > 0;
            if (dirty) {
                const confirmed = await UiHelper.confirm({
                    title: 'Änderungen verwerfen?',
                    content: html`<p>Es gibt ungespeicherte Änderungen an diesem Linkboard.
                        Wirklich verwerfen?</p>`,
                    confirmLabel: 'Verwerfen',
                    cancelLabel: 'Weiter bearbeiten',
                });
                if (!confirmed) return;
            }

            // The reload is a real read here (see `_reloadTiles`); a failure
            // must neither become an unhandled rejection nor keep the lock —
            // the User already confirmed discarding, so leaving edit mode
            // proceeds regardless.
            try {
                await this._reloadTiles();
            } catch (reloadError) {
                console.error(`Failed to reload the board «${this.linkviewUid}» after cancelling.`, reloadError);
                UiHelper.notify({
                    level: 'error',
                    title: 'Verwerfen unvollständig',
                    content: html`<p>Das Board konnte nicht neu geladen werden — verworfene Änderungen werden
                        möglicherweise noch angezeigt. Bitte die Seite neu laden.</p>`,
                    closable: true,
                });
            }
            await this._linkviewRepo.releaseLock(this.linkviewUid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${this.linkviewUid}» after cancelling.`, releaseError);
                this._notifyReleaseFailed('cancel');
            });
        } finally {
            this._cancelling = false;
        }
    }

    /** Applies a lock-state change: gates Gridstack's drag/resize via
     *  `setStatic()`, updates `.locked` on every already-mounted tile
     *  component (Lit re-renders it reactively — building/dropping the
     *  edit/delete buttons per `AbstractTileComponent`'s own `when()`), and —
     *  when this is the step INTO edit mode — rebuilds the board from a fresh
     *  read (see `_reloadTiles`). */
    private _applyLockState(locked: boolean): void {
        const enteringEditMode = this._locked && !locked;

        this._locked = locked;
        this._grid?.setStatic(locked);
        for (const el of this._tileElements.values()) {
            el.locked = locked;
        }

        if (enteringEditMode) {
            void this._reloadTiles();
        }
    }

    /**
     * Rebuilds the board from what the server has RIGHT NOW — on the step
     * into edit mode, and (2026-09-17) on `_onRequestCancel` too, which
     * reuses this unchanged: it only depends on `_grid` existing, not on
     * lock state, and "the board on screen must match what the server
     * actually has" is exactly what a cancel wants as well.
     *
     * Why it has to happen here and not only in the repository: the repository
     * refreshes the state it compares a save against, but the board on screen
     * is what the user then edits and what the save ships as the target. If
     * only the comparison state were refreshed, taking over a board somebody
     * else had extended and saving without touching anything would send a
     * delete for every tile they had added — the protection inverted into the
     * data loss it exists to prevent.
     *
     * On the step INTO edit mode this costs no extra HTTP request — the
     * repository performed the read as part of acquiring the lock and hands
     * that same cached result out here. A `_onRequestCancel` call is a
     * genuine cache miss (the entry read was already consumed) and issues a
     * real fetch — which is exactly the point: showing the User the true
     * current server state, not a locally-remembered one.
     *
     * Simply leaving edit mode WITHOUT cancelling (e.g. `_onRequestSave`
     * after a successful save) still reads nothing — a read on every lock
     * change would make "one read on entry" meaningless.
     */
    private async _reloadTiles(): Promise<void> {
        // Before `firstUpdated()` has created the grid there is nothing to
        // mount onto, and nothing is lost: its own read consumes the very same
        // edit-entry result. This is reachable — a board that is already
        // unlocked when the component mounts flips the state from the
        // `items$` subscription in `connectedCallback()`, which runs first.
        if (!this._grid) return;

        const tiles = await this._linkviewRepo.getTiles(this.linkviewUid);
        // A navigation away while the read was in flight nulls the grid.
        if (!this._grid) return;

        this._mountAll(tiles);
    }

    /**
     * Opens `ha-dialog-link-tile-edit` for a new tile (`tileId === null`) or
     * to edit an existing one. `DialogHelper` owns the CLOSE/CANCEL teardown
     * (same as every other non-entity dialog); the OKAY listener here is
     * this component's own, since the dialog's payload is a plain
     * `LinkTileDraft`, not an `ICommand` `DialogHelper`/`UiHelper` could
     * forward anywhere generic.
     */
    private _openTileDialog(tileId: string | null): void {
        // Review 0025-01, MINOR: while LOCKED, neither the edit/delete buttons
        // (gated in AbstractTileComponent) nor the toolbar's "+ Neue Kachel"
        // button (only rendered while unlocked, `o2d-linkview.view.ts`)
        // should reach this — but a locked board must stay non-editable
        // through THIS method itself too, not only via the UI affordances
        // that normally call it (defense in depth against e.g. a stale
        // addTileRequested$ race with a lock change, or a future second
        // caller).
        if (this._locked) return;

        const existingContent = tileId ? this._content.get(tileId) : undefined;
        // Reuses applyDraft's null→omitted-field conversion (LinkTileDraft's
        // fields are `string | null`, LinkTile's are `string | undefined` —
        // a plain spread would assign `null` where TS expects `undefined`).
        // x/y/w/h/text are placeholders here; the dialog never reads them —
        // `text` is an empty DOCUMENT, not `''`, only to satisfy `LinkTile.text`'s
        // type (review 0030-02, MAJOR: a leftover `''` from before the Delta
        // migration broke `tsc --strict`, invisible to esbuild's own
        // type-free `vite build`).
        const tile = existingContent && tileId
            ? LinkTileHelper.applyDraft({id: tileId, text: RteValueHelper.empty(), x: 0, y: 0, w: 0, h: 0}, existingContent)
            : null;
        const dialog = DialogHelper.showDialog('ha-dialog-link-tile-edit', {tile}) as HaDialogLinkTileEditComponent | null;
        if (!dialog) return;

        // See `_tileDialogSubscription`'s own doc comment: unsubscribe
        // whatever a previous `_openTileDialog()` call left listening on
        // this (possibly reused) dialog element before starting a new one,
        // so at most one ever fires per Speichern-click.
        this._tileDialogSubscription?.unsubscribe();
        this._tileDialogSubscription = fromEvent(dialog, 'dialog.message')
            .pipe(takeUntil(this._disconnected$))
            .subscribe((event) => {
                const detail = (event as CustomEvent<{ value: DialogCommand<LinkTileDraft> }>).detail;
                if (detail.value.command !== DialogCommandName.OKAY) return;
                if (tileId) {
                    void this._updateTile(tileId, detail.value.payload);
                } else {
                    void this._createTile(detail.value.payload);
                }
            });
    }

    /** Review 0025-02, MINOR: `ha-dialog-link-tile-edit._onClickSave()`
     *  closes unconditionally — if the board got locked (e.g. taken over by
     *  someone else) while the dialog was still open,
     *  `_createTile`/`_updateTile` below discard the save silently.
     *  Surfaced instead, rather than dropped. */
    private _notifyLockedDiscard(): void {
        UiHelper.notify({
            level: 'info',
            title: 'Nicht gespeichert',
            content: html`<p>Das Board wurde zwischenzeitlich gesperrt — diese Änderung wurde verworfen.</p>`,
            closable: true,
        });
    }

    private async _createTile(draft: LinkTileDraft): Promise<void> {
        if (this._locked) {
            this._notifyLockedDiscard();
            return;
        }
        if (!this._grid || !LinkTileHelper.isValidDraft(draft.text)) return;

        const tile = LinkTileHelper.createTile(draft);
        this._rememberContent(tile.id, draft, tile);
        const widget: GridStackWidget = {id: tile.id, w: tile.w, h: tile.h, autoPosition: true};
        const el = this._grid.addWidget(widget);
        if (el) this._mountTile(el, tile.id);
    }

    private async _updateTile(tileId: string, draft: LinkTileDraft): Promise<void> {
        if (this._locked) {
            this._notifyLockedDiscard();
            return;
        }
        // Review 0025-02, MINOR: kept consistent with `_createTile`/
        // `_removeTile`'s own `!this._grid` guard — the class doc comment
        // promises the lock check "in all four methods" uniformly, not one
        // implicitly relying on a guard elsewhere.
        if (!this._grid || !LinkTileHelper.isValidDraft(draft.text)) return;

        // Review 0025-01, MAJOR: normalize/trim through the same
        // `LinkTileHelper` path `_createTile` already uses, instead of
        // writing the raw draft straight into `_content`. Without this, a
        // schema-less URL ("example.com") rendered a broken relative link
        // immediately after editing, then silently became the correct
        // "https://example.com" only after a reload (the save path already
        // normalized it for storage, but that normalized value never flowed
        // back into `_content` for the live render) — live render and
        // persisted state disagreed on a page that was never reloaded.
        // x/y/w/h/text are placeholders (see `_openTileDialog`'s own comment
        // on the same pattern) — `applyDraft` only reads `draft`'s fields here.
        const tile = LinkTileHelper.applyDraft({id: tileId, text: RteValueHelper.empty(), x: 0, y: 0, w: 0, h: 0}, draft);
        this._rememberContent(tileId, draft, tile);
        const gridItemEl = this._gridElRef.value?.querySelector<GridItemHTMLElement>(`[gs-id="${tileId}"]`);
        if (gridItemEl) this._mountTile(gridItemEl, tileId);
    }

    /**
     * Instantiates the `AbstractTileComponent` subclass matching
     * `content.target?.kind` (via `TILE_COMPONENT_BY_KIND`, falling back to
     * `LinkTilePlainComponent` when unset or unrecognized — an
     * `UnknownLinkTarget`'s `kind` never matches a registry entry, so a tile
     * whose data survived unrecognized (see `LinkTargetSerializer`) simply
     * renders as a plain, non-clickable tile until the user re-edits it),
     * sets its properties, wires the shared `TileEventName.EDIT`/`DELETE`
     * events, and swaps it into the Gridstack item's content host
     * ATOMICALLY (`replaceChildren`, not a clear-then-append) so there is
     * never a frame with no tile at all.
     */
    private _mountTile(gridItemEl: GridItemHTMLElement, tileId: string): void {
        const content = this._content.get(tileId);
        const host = gridItemEl.querySelector<HTMLElement>('.grid-stack-item-content');
        if (!content || !host) return;

        host.style.backgroundColor = content.color ?? '';
        // The stored image is an asset-store reference and is rendered straight
        // from it. A freshly picked file has no reference yet — it is not
        // uploaded until the board is saved — so until then the local data-URL
        // preview stands in, which is the only way the user sees what they
        // picked before saving.
        // Neither an asset URL nor a data: URL can contain a `"`, so quoting it
        // plainly is safe — CSS.escape() is for IDENTIFIERS (selectors), not
        // url() contents, and would mangle a base64 payload's own `+`/`/`/`=`.
        const imageUrl = content.imageAssetUid
            ? buildAssetSrc(content.imageAssetUid)
            : content.imageDataUrl;
        host.style.backgroundImage = imageUrl ? `url("${imageUrl}")` : '';

        const kind = content.target?.kind;
        const TileClass = (kind && TILE_COMPONENT_BY_KIND[kind]) ?? LinkTilePlainComponent;
        const el = new TileClass();
        el.chrome = content;
        el.target = content.target ?? null;
        el.locked = this._locked;
        el.addEventListener(TileEventName.EDIT, () => this._openTileDialog(tileId));
        el.addEventListener(TileEventName.DELETE, () => void this._removeTile(tileId));

        this._tileElements.set(tileId, el);
        host.replaceChildren(el);
    }

    /**
     * Same confirm dialog (`UiHelper.confirm` → `<ha-confirm>`) and the same
     * "Wirklich löschen?" / "Ja, jetzt löschen!" / "Nein, abbrechen!" wording
     * as the issue delete dialog (`ha-dialog-issue-edit-orchestrator`'s
     * `_onClickSelectedEntityDeleteButton`) — only the body text names the
     * tile instead of the task.
     */
    private async _removeTile(tileId: string): Promise<void> {
        if (this._locked || !this._grid) return;
        const el = this._gridElRef.value?.querySelector<GridItemHTMLElement>(`[gs-id="${tileId}"]`);
        if (!el) return;

        const confirmed = await UiHelper.confirm({
            title: 'Wirklich löschen?',
            content: html`<p>Die Kachel wird gelöscht.</p>`,
            confirmLabel: 'Ja, jetzt löschen!',
            cancelLabel: 'Nein, abbrechen!'
        });
        if (!confirmed) return;

        this._grid.removeWidget(el);
        this._content.delete(tileId);
        this._tileElements.delete(tileId);
        // A tile that is gone must not still upload an image at the next save.
        this._pendingImages.delete(tileId);
    }

    /**
     * The whole save: the current arrangement plus the properties each tile
     * carries, handed to the repository as ONE target state.
     *
     * This is the only thing that persists anything. It runs from the save
     * button (`_onRequestSave`) and from nowhere else. On success, also
     * releases the edit lock — see `_onRequestSave`'s own doc comment for
     * the full reasoning and the ORDER (release awaited before the
     * `_locked` transition).
     */
    private async _save(): Promise<void> {
        if (!this._grid || !this.linkviewUid) return;

        // Every image first, and ALL of them before a single command goes out
        // — a half-saved board is worse than an unsaved one.
        if (!await this._uploadPendingImages()) return;

        const saved = await this._linkviewRepo.saveTiles(this.linkviewUid, this._collectTiles());
        if (!saved) {
            this._notifySaveFailed();
            return;
        }

        await this._linkviewRepo.releaseLock(this.linkviewUid).catch((releaseError: unknown) => {
            console.error(`Failed to release the edit lock for «${this.linkviewUid}» after saving.`, releaseError);
            this._notifyReleaseFailed('save');
        });
    }

    /** See `_onRequestSave`'s doc comment: the board stays in edit mode after
     *  a failed release, so the User needs to know what happened and what
     *  retries it. */
    private _notifyReleaseFailed(after: 'save' | 'cancel'): void {
        UiHelper.notify({
            level: 'error',
            title: after === 'save' ? 'Gespeichert, aber nicht beendet' : 'Bearbeitung nicht beendet',
            content: after === 'save'
                ? html`<p>Die Änderungen wurden gespeichert, aber die Bearbeitung konnte nicht beendet werden.
                    Bitte noch einmal auf „Speichern“ oder „Abbrechen“ klicken.</p>`
                : html`<p>Die Änderungen wurden verworfen, aber die Bearbeitung konnte nicht beendet werden.
                    Bitte noch einmal auf „Abbrechen“ klicken.</p>`,
            closable: true,
        });
    }

    /**
     * Puts every picked-but-not-yet-stored image into the asset store and
     * writes the returned reference onto its tile. Answers whether the save may
     * continue.
     *
     * The uploads happen OUTSIDE the one transaction the commands travel in —
     * they have to, since a command payload needs the uid the upload returns.
     * So a failure here means: no row changed, but an asset may already exist
     * that nothing references. That residue is named rather than fixed (the
     * store has no delete path at all); it costs nothing to retry, because the
     * endpoint is content-addressed and the same bytes resolve to the same row.
     *
     * Nothing is written back and no pending file is dropped until every upload
     * has succeeded, so a failed save leaves the user's picks exactly as they
     * were.
     */
    private async _uploadPendingImages(): Promise<boolean> {
        if (this._pendingImages.size === 0) return true;

        const uploaded = new Map<string, string>();
        for (const [tileId, file] of this._pendingImages) {
            try {
                const result = await this._backendClient.uploadFile(file);
                // The uid is used EXACTLY as returned. It is a ShortId, and
                // `/api/v1/files/<uid>` resolves that spelling and no other.
                uploaded.set(tileId, result.uid);
            } catch (error) {
                console.error('Failed to upload a Linkview tile image', error);
                this._notifyUploadFailed(tileId);
                return false;
            }
        }

        for (const [tileId, imageAssetUid] of uploaded) {
            const content = this._content.get(tileId);
            if (content) {
                this._content.set(tileId, {...content, imageAssetUid, imageDataUrl: null, imageFile: null});
            }
            this._pendingImages.delete(tileId);
        }

        return true;
    }

    /**
     * Names the tile whose image failed. A generic "saving failed" would leave
     * the user hunting through a board for which of their picks broke it.
     *
     * Review 0030-01, MAJOR: since the tile-text RTE change `text` is not
     * reliably a string — it can be a rich-text document object (measured in
     * the stock: com holds two), and interpolating that directly rendered
     * literally "[object Object]" here. `LinkTileHelper.plainText()` strips it back
     * down to the label; falls back to `tileId` only when there is genuinely
     * no tracked content (or no real text in it) to name the tile by.
     */
    private _notifyUploadFailed(tileId: string): void {
        const text = this._content.get(tileId)?.text;
        const label = LinkTileHelper.plainText(text) || tileId;
        UiHelper.notify({
            level: 'error',
            title: 'Nicht gespeichert',
            content: html`<p>Das Bild der Kachel „${label}“ konnte nicht hochgeladen werden. Es wurde nichts
                gespeichert — bitte noch einmal versuchen.</p>`,
            closable: true,
        });
    }

    /**
     * Stores a tile's user-authored fields, and parks a freshly picked file in
     * `_pendingImages` instead of in `_content`: the file is transient state
     * belonging to the next save, while `_content` is the tile as it will be
     * persisted. Keeping the two apart is what stops a `File` from ever
     * reaching the wire.
     */
    private _rememberContent(tileId: string, draft: LinkTileDraft, tile: LinkTile): void {
        if (draft.imageFile) {
            this._pendingImages.set(tileId, draft.imageFile);
        } else if (!draft.imageAssetUid) {
            // The image was explicitly cleared in the dialog.
            this._pendingImages.delete(tileId);
        }

        this._content.set(tileId, {
            ...tileToDraft(tile),
            imageDataUrl: draft.imageDataUrl,
            imageFile: null,
        });
    }

    /**
     * Reads the current live layout straight off Gridstack (the single source
     * of truth for x/y/w/h once dragging/resizing has happened) and merges in
     * the properties each tile carries.
     *
     * `save(false)` is called with EXACTLY ONE argument. Passing the current
     * column count as a second one looks helpful and is the opposite: it
     * switches the result to the layout as currently DISPLAYED, so saving on a
     * narrow window would write the collapsed arrangement back for everybody.
     */
    private _collectTiles(): LinkTile[] {
        if (!this._grid) return [];

        const nodes = this._grid.save(false) as GridStackWidget[];
        return nodes
            .filter((n): n is GridStackWidget & { id: string } => !!n.id)
            .map((n) => {
                const content = this._content.get(n.id);
                return LinkTileHelper.applyDraft(
                    {
                        id: n.id,
                        text: content?.text ?? RteValueHelper.empty(),
                        x: n.x ?? 0,
                        y: n.y ?? 0,
                        // Gridstack omits a size of 1 (or equal to the node's
                        // minimum) from `save()` — see
                        // `LinkTileHelper.sizeFromSavedNode` for why the gap
                        // must not be filled with the new-tile default.
                        ...LinkTileHelper.sizeFromSavedNode(n),
                    },
                    content ?? EMPTY_DRAFT,
                );
            });
    }

    /**
     * A failed save says WHICH of the two things happened, and neither text is
     * the one this component used to show.
     *
     * The old message named the browser's storage quota, because storage WAS
     * the browser. Now that the board lives on the server, that same sentence
     * would tell a user whose edit lock was taken away that their browser is
     * full — advice that is not merely useless but points away from the actual
     * cause.
     *
     * The status is read off the concrete repository rather than the contract:
     * `saveTiles` returns a bare boolean (Anke's contract, unchanged), and a
     * boolean cannot carry the difference. A repository that does not offer it
     * is treated as the general failure, which is the safe direction.
     */
    private _notifySaveFailed(): void {
        const failure = (this._linkviewRepo as Partial<BackendLinkviewRepository>).lastSaveFailure ?? 'error';

        UiHelper.notify({
            level: 'error',
            title: 'Nicht gespeichert',
            content: failure === 'conflict'
                ? html`<p>Die Bearbeitungssperre für dieses Linkboard ist verloren gegangen — vermutlich
                    bearbeitet es jemand anderes. Es wurde nichts gespeichert. Bitte das Board neu laden.</p>`
                : html`<p>Die Änderungen konnten nicht gespeichert werden. Bitte noch einmal versuchen.</p>`,
            closable: true,
        });
    }

}

/** A tile whose content is not (or no longer) tracked — every field explicitly
 *  empty rather than absent, so `applyDraft` clears rather than keeps. */
const EMPTY_DRAFT: LinkTileDraft = {
    text: RteValueHelper.empty(),
    target: null,
    imageDataUrl: null,
    imageFile: null,
    imageAssetUid: null,
    color: null,
    icon: null,
};

function tileToDraft(tile: LinkTile): LinkTileDraft {
    return {
        text: tile.text,
        target: tile.target ?? null,
        // A persisted tile carries a reference, never an image and never a
        // pending file — both of those only ever exist between a pick and the
        // save that follows it.
        imageDataUrl: null,
        imageFile: null,
        imageAssetUid: tile.imageAssetUid ?? null,
        color: tile.color ?? null,
        icon: tile.icon ?? null,
    };
}
